import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../Revenue/revenue.css";
import "./gifts.css";
import { errorMessage } from "../Revenue/revenueApi";
import { Loading } from "../Revenue/parts";
import { fmt } from "../Revenue/format";
import {
  ANIM_ACCEPT,
  GIFT_LANGS,
  IMG_ACCEPT,
  animKind,
  deleteGift,
  fileSize,
  listGifts,
  saveGift,
} from "./giftsApi";

const missingLangs = (g) => GIFT_LANGS.filter(({ code }) => !(g.name_translations || {})[code]);

/// Live gifts: add, edit and delete the gifts viewers send in a live, with
/// the gift name in every app language (English is required, the others
/// fall back to English in the app when left empty).
export default function Gifts() {
  const [gifts, setGifts] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [editing, setEditing] = useState(null); // null | "new" | gift
  const [deleting, setDeleting] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      setGifts(await listGifts());
    } catch (e) {
      setGifts([]);
      setError(errorMessage(e, "Could not load gifts"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(""), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  const missingCount = useMemo(() => (gifts || []).filter((g) => missingLangs(g).length).length, [gifts]);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = (gifts || []).filter((g) => {
      if (filter === "missing" && !missingLangs(g).length) return false;
      if (!term) return true;
      if (String(g.id) === term) return true;
      return Object.values({ en: g.name, ...(g.name_translations || {}) }).some((v) =>
        String(v || "").toLowerCase().includes(term)
      );
    });
    if (sort === "price_asc") list = [...list].sort((a, b) => Number(a.coins) - Number(b.coins));
    if (sort === "price_desc") list = [...list].sort((a, b) => Number(b.coins) - Number(a.coins));
    return list;
  }, [gifts, q, filter, sort]);

  const onSaved = (msg) => {
    setEditing(null);
    setNotice(msg);
    load();
  };

  return (
    <div className="rv-wrap gf-page">
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-4">
        <div>
          <h2 className="rv-title mb-1">Gifts</h2>
          <p className="rv-sub">
            Gifts sent in lives. The app shows each name in the viewer's language — empty languages fall back to
            English.
          </p>
        </div>
        <button type="button" className="rv-btn" onClick={() => setEditing("new")}>
          + Add gift
        </button>
      </div>

      {notice && <div className="rv-success mb-3">{notice}</div>}
      {error && <div className="rv-error mb-3">{error}</div>}

      <div className="rv-panel">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <input
            className="rv-input gf-search"
            placeholder="Search by name in any language or ID"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="rv-seg">
            <button type="button" className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")}>
              All {gifts ? `(${gifts.length})` : ""}
            </button>
            <button
              type="button"
              className={filter === "missing" ? "active" : ""}
              onClick={() => setFilter("missing")}
            >
              Missing names ({missingCount})
            </button>
          </div>
          <select className="rv-select gf-sort" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="newest">Newest first</option>
            <option value="price_asc">Price: low to high</option>
            <option value="price_desc">Price: high to low</option>
          </select>
        </div>

        {gifts === null ? (
          <Loading />
        ) : !rows.length ? (
          <div className="rv-empty">{gifts.length ? "No gifts match." : "No gifts yet. Add the first one."}</div>
        ) : (
          <div className="rv-table-wrap">
            <table className="rv-table">
              <thead>
                <tr>
                  <th>Gift</th>
                  <th>Names</th>
                  <th className="rv-num">Price (coins)</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((g) => {
                  const tr = g.name_translations || {};
                  return (
                    <tr key={g.id}>
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <button
                            type="button"
                            className="gf-thumb"
                            title="Play animation"
                            onClick={() => setPreview(g)}
                          >
                            {g.img ? <img src={g.img} alt={g.name} loading="lazy" /> : <span>?</span>}
                            {g.svg && <i className="gf-play">▶</i>}
                          </button>
                          <div className="min-w-0">
                            <div className="rv-user-name">{g.name}</div>
                            <div className="rv-user-sub">ID {g.id}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="gf-langs">
                          {GIFT_LANGS.map(({ code, label }) => (
                            <span
                              key={code}
                              className={`rv-badge ${tr[code] ? "rv-badge-green" : "rv-badge-grey"}`}
                              title={tr[code] ? `${label}: ${tr[code]}` : `${label}: empty (shows English)`}
                            >
                              {code.toUpperCase()}
                              {tr[code] ? ` · ${tr[code]}` : " · —"}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="rv-num">{fmt(g.coins)}</td>
                      <td className="text-end gf-actions">
                        <button type="button" className="rv-btn ghost rv-btn-sm" onClick={() => setEditing(g)}>
                          Edit
                        </button>
                        <button type="button" className="rv-btn danger rv-btn-sm" onClick={() => setDeleting(g)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <GiftForm
          gift={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
      {deleting && (
        <DeleteGift
          gift={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            setNotice("Gift deleted");
            load();
          }}
        />
      )}
      {preview && <PreviewGift gift={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function Modal({ children, onClose, busy, wide }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);
  return (
    <div className="rv-modal-backdrop" onClick={() => !busy && onClose()}>
      <div
        className={`rv-modal ${wide ? "gf-modal-wide" : ""}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </div>
  );
}

/// Object URL for a picked file, revoked when it changes or unmounts.
function useObjectUrl(file) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return undefined;
    }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url;
}

function AnimPreview({ src, kind }) {
  if (!src) return null;
  if (kind === "image") return <img className="gf-anim" src={src} alt="animation" />;
  if (kind === "audio") return <audio className="w-100" src={src} controls />;
  return <video className="gf-anim" src={src} controls autoPlay loop muted playsInline />;
}

function GiftForm({ gift, onClose, onSaved }) {
  const isNew = !gift;
  const [name, setName] = useState(gift?.name || "");
  const [coins, setCoins] = useState(gift ? String(gift.coins ?? "") : "");
  const [translations, setTranslations] = useState(() => {
    const tr = gift?.name_translations || {};
    return GIFT_LANGS.reduce((acc, { code }) => ({ ...acc, [code]: tr[code] || "" }), {});
  });
  const [img, setImg] = useState(null);
  const [svg, setSvg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [err, setErr] = useState("");
  const imgUrl = useObjectUrl(img);
  const svgUrl = useObjectUrl(svg);

  // No size limit: video-worker optimises the file after the upload.
  const pickFile = (setter) => (e) => {
    setErr("");
    setter(e.target.files?.[0] || null);
  };

  const submit = async (e) => {
    e.preventDefault();
    const c = Number(coins);
    if (!name.trim()) return setErr("English name is required.");
    if (coins === "" || !Number.isFinite(c) || c < 0) return setErr("Price must be a number of coins, 0 or more.");
    if (isNew && (!img || !svg)) return setErr("Image and animation are both required for a new gift.");
    setBusy(true);
    setErr("");
    setProgress(img || svg ? 0 : null);
    try {
      await saveGift(isNew ? null : gift.id, { name, coins, translations, img, svg }, setProgress);
      onSaved(
        img || svg
          ? `${isNew ? "Gift added" : "Gift updated"} — the files are being optimised and will be ready in the app in a minute`
          : isNew
          ? "Gift added"
          : "Gift updated"
      );
    } catch (e2) {
      setErr(errorMessage(e2, e2?.message || "Could not save the gift"));
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <Modal onClose={onClose} busy={busy} wide>
      <form onSubmit={submit}>
        <div className="rv-panel-title">{isNew ? "Add gift" : `Edit gift #${gift.id}`}</div>

        <div className="row g-3">
          <div className="col-md-8">
            <label className="rv-label">Name — English *</label>
            <input className="rv-input" value={name} maxLength={255} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="col-md-4">
            <label className="rv-label">Price (coins) *</label>
            <input
              className="rv-input"
              type="number"
              min="0"
              step="any"
              inputMode="decimal"
              value={coins}
              onChange={(e) => setCoins(e.target.value)}
            />
          </div>

          {GIFT_LANGS.map(({ code, label, native, dir }) => (
            <div className="col-md-6" key={code}>
              <label className="rv-label">
                Name — {label} <span className="gf-native">({native})</span>
              </label>
              <input
                className="rv-input"
                dir={dir}
                lang={code}
                maxLength={255}
                placeholder={name ? `Empty = "${name}"` : "Optional"}
                value={translations[code]}
                onChange={(e) => setTranslations((t) => ({ ...t, [code]: e.target.value }))}
              />
            </div>
          ))}

          <div className="col-md-6">
            <label className="rv-label">
              Image (PNG/JPG){isNew ? " *" : " — leave empty to keep"}
            </label>
            <input className="rv-input" type="file" accept={IMG_ACCEPT} onChange={pickFile(setImg)} />
            {img && <div className="rv-user-sub mt-1">{fileSize(img.size)}</div>}
            <div className="gf-file-preview">
              {imgUrl || gift?.img ? <img src={imgUrl || gift.img} alt="gift" /> : null}
            </div>
          </div>
          <div className="col-md-6">
            <label className="rv-label">
              Animation (MP4/GIF/AAC/M4A){isNew ? " *" : " — leave empty to keep"}
            </label>
            <input className="rv-input" type="file" accept={ANIM_ACCEPT} onChange={pickFile(setSvg)} />
            {svg && <div className="rv-user-sub mt-1">{fileSize(svg.size)}</div>}
            <div className="gf-file-preview">
              <AnimPreview src={svgUrl || gift?.svg} kind={animKind(svg ? svg.type || svg.name : gift?.svg)} />
            </div>
          </div>
        </div>

        <p className="rv-sub mt-3 mb-0">
          No size limit — after upload every file is optimised automatically (quality kept) and pushed to the CDN.
        </p>

        {progress !== null && (
          <div className="rv-progress sm mt-3">
            <span style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
        )}
        {err && <div className="rv-error mt-3">{err}</div>}

        <div className="d-flex justify-content-end gap-2 mt-4">
          <button type="button" className="rv-btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="rv-btn" disabled={busy}>
            {busy
              ? progress !== null && progress < 1
                ? `Uploading ${Math.round(progress * 100)}%`
                : "Saving…"
              : isNew
              ? "Add gift"
              : "Save changes"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function DeleteGift({ gift, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const confirm = async () => {
    setBusy(true);
    setErr("");
    try {
      await deleteGift(gift.id);
      onDeleted();
    } catch (e) {
      setErr(errorMessage(e, "Could not delete the gift"));
      setBusy(false);
    }
  };
  return (
    <Modal onClose={onClose} busy={busy}>
      <div className="rv-panel-title">Delete "{gift.name}"?</div>
      <p className="rv-sub">
        The gift disappears from the live gift panel and its image and animation files are deleted. Past gift
        history stays in revenue reports.
      </p>
      {err && <div className="rv-error mt-3">{err}</div>}
      <div className="d-flex justify-content-end gap-2 mt-4">
        <button type="button" className="rv-btn ghost" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="rv-btn danger" onClick={confirm} disabled={busy}>
          {busy ? "Deleting…" : "Delete"}
        </button>
      </div>
    </Modal>
  );
}

function PreviewGift({ gift, onClose }) {
  return (
    <Modal onClose={onClose}>
      <div className="rv-panel-title">{gift.name}</div>
      <div className="gf-file-preview lg">
        {gift.svg ? (
          <AnimPreview src={gift.svg} kind={animKind(gift.svg)} />
        ) : (
          gift.img && <img src={gift.img} alt={gift.name} />
        )}
      </div>
      <div className="d-flex justify-content-end mt-3">
        <button type="button" className="rv-btn ghost" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}
